import * as THREE from 'three';

export interface CelebrationVfxDiagnostics {
  readonly activeParticles: number;
  readonly activeRings: number;
  readonly bursts: number;
  readonly jackpotBursts: number;
}

type Particle = {
  active: boolean;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  life: number;
  duration: number;
  spin: number;
  size: number;
};

type Ring = {
  mesh: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  active: boolean;
  life: number;
  duration: number;
};

const PARTICLE_CAPACITY = 96;
const HIDDEN_MATRIX = new THREE.Matrix4().makeScale(0, 0, 0);

/**
 * A small pooled celebration layer for tray wins, card pickups, and jackpots.
 * It uses one instanced draw for all particles and never allocates during update.
 */
export class CelebrationVfx {
  readonly root = new THREE.Group();

  private readonly geometry = new THREE.OctahedronGeometry(0.055, 0);
  private readonly material = new THREE.MeshBasicMaterial({
    color: '#ffffff',
    transparent: true,
    opacity: 0.95,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexColors: true,
    toneMapped: false,
  });
  private readonly instances = new THREE.InstancedMesh(
    this.geometry,
    this.material,
    PARTICLE_CAPACITY,
  );
  private readonly particles: Particle[] = [];
  private readonly rings: Ring[] = [];
  private readonly matrix = new THREE.Matrix4();
  private readonly quaternion = new THREE.Quaternion();
  private readonly scale = new THREE.Vector3();
  private readonly axis = new THREE.Vector3(0.4, 1, 0.2).normalize();
  private particleCursor = 0;
  private burstCount = 0;
  private jackpotBurstCount = 0;
  private disposed = false;

  constructor(parent: THREE.Object3D) {
    this.root.name = 'Celebration VFX';
    this.instances.name = 'Pooled celebration particles';
    this.instances.frustumCulled = false;
    this.instances.renderOrder = 60;
    this.instances.count = PARTICLE_CAPACITY;
    this.root.add(this.instances);
    parent.add(this.root);

    for (let index = 0; index < PARTICLE_CAPACITY; index += 1) {
      this.particles.push({
        active: false,
        position: new THREE.Vector3(),
        velocity: new THREE.Vector3(),
        life: 0,
        duration: 1,
        spin: 0,
        size: 1,
      });
      this.instances.setMatrixAt(index, HIDDEN_MATRIX);
      this.instances.setColorAt(index, new THREE.Color('#ffffff'));
    }
    this.instances.instanceMatrix.needsUpdate = true;
    if (this.instances.instanceColor) this.instances.instanceColor.needsUpdate = true;

    const colors = ['#ffd765', '#ff4da6', '#37e8ff'];
    colors.forEach((color, index) => {
      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        toneMapped: false,
      });
      const mesh = new THREE.Mesh(new THREE.RingGeometry(0.45, 0.49, 48), material);
      mesh.name = `Jackpot pulse ring ${index + 1}`;
      mesh.rotation.x = -Math.PI * 0.5;
      mesh.visible = false;
      mesh.renderOrder = 59;
      this.root.add(mesh);
      this.rings.push({ mesh, active: false, life: 0, duration: 1 });
    });
  }

  triggerCoinWin(position: THREE.Vector3 | readonly [number, number, number]): void {
    this.burst(position, 14, ['#fff4aa', '#ffc73c', '#ffffff'], 1.2, 0.72);
  }

  triggerCardWin(position: THREE.Vector3 | readonly [number, number, number]): void {
    this.burst(position, 28, ['#ff4da6', '#35ddff', '#ffe36d'], 1.75, 1.05);
  }

  triggerJackpot(position: THREE.Vector3 | readonly [number, number, number]): void {
    this.jackpotBurstCount += 1;
    this.burst(position, PARTICLE_CAPACITY, ['#ffcf45', '#ff3e9e', '#2fe8ff', '#ffffff'], 3.2, 2.15);
    const origin = readPosition(position);
    this.rings.forEach((ring, index) => {
      ring.active = true;
      ring.life = -index * 0.16;
      ring.duration = 1.45;
      ring.mesh.position.copy(origin);
      ring.mesh.scale.setScalar(0.45);
      ring.mesh.material.opacity = 0;
      ring.mesh.visible = true;
    });
  }

  update(deltaSeconds: number): void {
    if (this.disposed) return;
    const delta = THREE.MathUtils.clamp(deltaSeconds, 0, 0.05);
    let dirty = false;
    for (let index = 0; index < this.particles.length; index += 1) {
      const particle = this.particles[index];
      if (!particle.active) continue;
      particle.life += delta;
      if (particle.life >= particle.duration) {
        particle.active = false;
        this.instances.setMatrixAt(index, HIDDEN_MATRIX);
        dirty = true;
        continue;
      }
      particle.velocity.y -= delta * 2.3;
      particle.velocity.multiplyScalar(Math.max(0, 1 - delta * 0.42));
      particle.position.addScaledVector(particle.velocity, delta);
      const progress = particle.life / particle.duration;
      const envelope = Math.sin(Math.PI * Math.min(1, progress));
      const size = particle.size * (0.25 + envelope * 0.9);
      this.quaternion.setFromAxisAngle(this.axis, particle.spin + particle.life * 5.2);
      this.scale.set(size, size, size * 0.55);
      this.matrix.compose(particle.position, this.quaternion, this.scale);
      this.instances.setMatrixAt(index, this.matrix);
      dirty = true;
    }
    if (dirty) this.instances.instanceMatrix.needsUpdate = true;

    for (const ring of this.rings) {
      if (!ring.active) continue;
      ring.life += delta;
      if (ring.life < 0) continue;
      if (ring.life >= ring.duration) {
        ring.active = false;
        ring.mesh.visible = false;
        ring.mesh.material.opacity = 0;
        continue;
      }
      const progress = ring.life / ring.duration;
      const scale = 0.45 + progress * 5.8;
      ring.mesh.scale.setScalar(scale);
      ring.mesh.material.opacity = Math.sin(Math.PI * progress) * 0.7;
    }
  }

  getDiagnostics(): CelebrationVfxDiagnostics {
    return Object.freeze({
      activeParticles: this.particles.filter((particle) => particle.active).length,
      activeRings: this.rings.filter((ring) => ring.active).length,
      bursts: this.burstCount,
      jackpotBursts: this.jackpotBurstCount,
    });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.root.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
    for (const ring of this.rings) {
      ring.mesh.geometry.dispose();
      ring.mesh.material.dispose();
    }
    this.root.clear();
  }

  private burst(
    position: THREE.Vector3 | readonly [number, number, number],
    count: number,
    colors: readonly string[],
    speed: number,
    duration: number,
  ): void {
    this.burstCount += 1;
    const origin = readPosition(position);
    const total = Math.min(PARTICLE_CAPACITY, Math.max(1, Math.floor(count)));
    for (let index = 0; index < total; index += 1) {
      const slot = this.particleCursor % PARTICLE_CAPACITY;
      this.particleCursor += 1;
      const particle = this.particles[slot];
      const angle = (index / total) * Math.PI * 2 + Math.random() * 0.35;
      const elevation = 0.25 + Math.random() * 1.05;
      const radial = speed * (0.45 + Math.random() * 0.7);
      particle.active = true;
      particle.position.copy(origin);
      particle.position.x += (Math.random() - 0.5) * 0.12;
      particle.position.z += (Math.random() - 0.5) * 0.12;
      particle.velocity.set(Math.cos(angle) * radial, elevation * speed, Math.sin(angle) * radial * 0.65);
      particle.life = 0;
      particle.duration = duration * (0.72 + Math.random() * 0.5);
      particle.spin = Math.random() * Math.PI * 2;
      particle.size = 0.7 + Math.random() * 1.25;
      this.instances.setColorAt(slot, new THREE.Color(colors[index % colors.length]));
    }
    this.instances.instanceMatrix.needsUpdate = true;
    if (this.instances.instanceColor) this.instances.instanceColor.needsUpdate = true;
  }
}

function readPosition(position: THREE.Vector3 | readonly [number, number, number]): THREE.Vector3 {
  return Array.isArray(position)
    ? new THREE.Vector3(position[0], position[1], position[2])
    : (position as THREE.Vector3).clone();
}
